import {
    createSelector,
    createEntityAdapter
} from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice"

const tasksAdapter = createEntityAdapter({
    sortComparer: (a, b) => (a.completed === b.completed) ? 0 : a.completed ? 1 : -1
})

const initialState = tasksAdapter.getInitialState()

export const tasksApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getTasks: builder.query({
            query: () => ({
                url: '/api/tasks',
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },
            }),
            transformResponse: responseData => {
                const loadedTasks = responseData.map(task => {
                    task.id = task._id
                    return task
                });
                return tasksAdapter.setAll(initialState, loadedTasks)
            },
            providesTags: (result, error, arg) => {
                if (result?.ids) {
                    return [
                        { type: 'Task', id: 'LIST' },
                        ...result.ids.map(id => ({ type: 'Task', id }))
                    ]
                } else return [{ type: 'Task', id: 'LIST' }]
            }
        }),
        addNewTask: builder.mutation({
            query: initialTask => ({
                url: '/api/tasks',
                method: 'POST',
                body: {
                    ...initialTask,
                }
            }),
            invalidatesTags: [
                { type: 'Task', id: "LIST" }
            ]
        }),
        duplicateTask: builder.mutation({
            query: initialTask => ({
                url: '/api/tasks/duplicate',
                method: 'POST',
                body: {
                    ...initialTask,
                }
            }),
            invalidatesTags: [
                { type: 'Task', id: "LIST" },
                { type: 'Action', id: "LIST" }
            ]
        }),
        duplicateAllTask: builder.mutation({
            query: initialTask => ({
                url: '/api/tasks/duplicate-all',
                method: 'POST',
                body: {
                    ...initialTask,
                }
            }),
            invalidatesTags: [
                { type: 'Task', id: "LIST" },
                { type: 'Action', id: "LIST" }
            ]
        }),
        updateTask: builder.mutation({
            query: initialTask => ({
                url: '/api/tasks',
                method: 'PATCH',
                body: {
                    ...initialTask,
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Task', id: arg.id }
            ]
        }),
        editMultiTask: builder.mutation({
            query: initialTask => ({
                url: '/api/tasks/edit-multi',
                method: 'POST',
                body: {
                    ...initialTask,
                }
            }),
            invalidatesTags: [
                { type: 'Task', id: "LIST" }
            ]
        }),
        deleteTask: builder.mutation({
            query: ({ id }) => ({
                url: `/api/tasks`,
                method: 'DELETE',
                body: { id }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Task', id: arg.id }
            ]
        }),
    }),
})

export const {
    useGetTasksQuery,
    useAddNewTaskMutation,
    useUpdateTaskMutation,
    useDeleteTaskMutation,
    useDuplicateTaskMutation,
    useDuplicateAllTaskMutation,
    useEditMultiTaskMutation
} = tasksApiSlice

// returns the query result object
export const selectTasksResult = tasksApiSlice.endpoints.getTasks.select()

// creates memoized selector
const selectTasksData = createSelector(
    selectTasksResult,
    tasksResult => tasksResult.data // normalized state object with ids & entities
)

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
    selectAll: selectAllTasks,
    selectById: selectTaskById,
    selectIds: selectTaskIds
    // Pass in a selector that returns the tasks slice of state
} = tasksAdapter.getSelectors(state => selectTasksData(state) ?? initialState)